import { useState, useEffect } from 'react';
import { ShieldCheck, Copy, CircleCheck as CheckCircle, Loader as Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

interface MfaEnrollProps {
  onEnrolled: () => void;
}

export default function MfaEnroll({ onEnrolled }: MfaEnrollProps) {
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    async function enroll() {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'X Manage Authenticator',
      });

      if (enrollError) {
        setError(enrollError.message);
        setEnrolling(false);
        return;
      }

      if (data) {
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
        setFactorId(data.id);
      }
      setEnrolling(false);
    }

    enroll();
  }, []);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { data: challengeData, error: challengeError } =
      await supabase.auth.mfa.challenge({ factorId });

    if (challengeError) {
      setError(challengeError.message);
      setLoading(false);
      return;
    }

    const { error: verifyError } = await supabase.auth.mfa.verify({
      factorId,
      challengeId: challengeData.id,
      code,
    });

    if (verifyError) {
      setError(verifyError.message);
      setLoading(false);
      return;
    }

    onEnrolled();
  }

  function handleCopy() {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (enrolling) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
          <p className="text-white/60 text-sm">Setting up two-factor authentication...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <div className="w-full max-w-md bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-teal-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Set Up 2FA</h2>
            <p className="text-xs text-white/60">Scan with your authenticator app</p>
          </div>
        </div>

        {error && (
          <div className="mb-5 p-3 bg-red-500/20 border border-red-400/30 rounded-xl">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        {qrCode && (
          <div className="flex justify-center mb-5">
            <div className="bg-white p-3 rounded-xl">
              <img src={qrCode} alt="2FA QR Code" className="w-48 h-48" />
            </div>
          </div>
        )}

        <div className="mb-5">
          <p className="text-xs text-white/60 mb-2">Or enter this code manually:</p>
          <div className="flex items-center gap-2 bg-slate-900/60 border border-slate-700/50 rounded-xl px-4 py-2.5">
            <code className="flex-1 text-sm text-teal-300 font-mono break-all select-all">
              {secret}
            </code>
            <button
              onClick={handleCopy}
              className="p-1.5 text-white/50 hover:text-white transition-colors shrink-0"
              title="Copy secret"
            >
              {copied ? <CheckCircle size={16} className="text-teal-400" /> : <Copy size={16} />}
            </button>
          </div>
        </div>

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label htmlFor="totp-code" className="block text-sm font-medium text-white mb-1.5">
              Verification code
            </label>
            <input
              id="totp-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              required
              className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-xl text-sm text-white text-center tracking-[0.5em] font-mono focus:ring-2 focus:ring-teal-400/30 focus:border-teal-400/50 outline-none transition-all duration-200 placeholder:text-white/40 placeholder:tracking-[0.5em]"
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length < 6}
            className="w-full bg-teal-500/30 glass-hover-teal backdrop-blur-xl border border-teal-400/30 text-white font-semibold py-2.5 rounded-xl disabled:bg-slate-500/30 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm shadow-lg shadow-teal-500/15"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <ShieldCheck size={16} />
                Verify & Activate
              </>
            )}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-white/50">
          Save this secret in your authenticator app. You will need it for every login.
        </p>
      </div>
    </div>
  );
}
